import React, { useContext, useState } from "react";
import {
  FiCheck,
  FiChevronLeft,
  FiLock,
  FiMinus,
  FiPlus,
  FiShoppingBag,
  FiTag,
  FiTrash2,
  FiTruck,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/cartpage.scss";

const DISCOUNT_CODE = siteConfig.cartPromotion?.code || "NATUR10";
const DISCOUNT_RATE = Number(siteConfig.cartPromotion?.discountRate) || 0;
const VAT_RATE = 0.19;

const fallbackMarkup = `
  <svg xmlns="http://www.w3.org/2000/svg" width="720" height="960" viewBox="0 0 720 960">
    <rect width="720" height="960" fill="#eeece4"/>
    <path d="M360 385c52-68 122-63 151-60-7 65-50 133-151 135-101-2-144-70-151-135 29-3 99-8 151 60Z" fill="#79856e" opacity=".42"/>
    <text x="360" y="535" text-anchor="middle" font-family="Arial,sans-serif" font-size="28" fill="#4e574c">Naturmode</text>
  </svg>`;
const PRODUCT_IMAGE_FALLBACK = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  fallbackMarkup
)}`;

const currencyFormatter = new Intl.NumberFormat(siteConfig.locale, {
  style: "currency",
  currency: siteConfig.currency,
});

const formatPrice = (value) => currencyFormatter.format(Number(value) || 0);

const handleImageError = (event) => {
  event.currentTarget.onerror = null;
  event.currentTarget.src = PRODUCT_IMAGE_FALLBACK;
};

const CartPage = () => {
  const cartState = useContext(CartContext) || {};
  const cartItems = Array.isArray(cartState.cartItems)
    ? cartState.cartItems
    : [];
  const removeFromCart = cartState.removeFromCart || (() => {});
  const updateQuantity = cartState.updateQuantity || (() => {});
  const [couponInput, setCouponInput] = useState("");
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [couponMessage, setCouponMessage] = useState(null);

  const validCartItems = cartItems
    .map((item) => ({
      ...item,
      product: productData.find((product) => product.id === item.id),
    }))
    .filter((item) => item.product);

  const itemCount = validCartItems.reduce(
    (total, item) => total + item.quantity,
    0
  );
  const subtotal = validCartItems.reduce(
    (total, item) => total + Number(item.product.price) * item.quantity,
    0
  );
  const discount = isCouponApplied ? subtotal * DISCOUNT_RATE : 0;
  const merchandiseTotal = Math.max(0, subtotal - discount);
  const freeShippingThreshold = Number(siteConfig.freeShippingThreshold) || 0;
  const shippingFee = Number(siteConfig.shippingFee) || 0;
  const hasFreeShipping = merchandiseTotal >= freeShippingThreshold;
  const shipping = hasFreeShipping ? 0 : shippingFee;
  const total = merchandiseTotal + shipping;
  const includedVat = total - total / (1 + VAT_RATE);
  const amountUntilFreeShipping = Math.max(
    0,
    freeShippingThreshold - merchandiseTotal
  );
  const shippingProgress = freeShippingThreshold
    ? Math.min(100, (merchandiseTotal / freeShippingThreshold) * 100)
    : 100;

  const handleCouponSubmit = (event) => {
    event.preventDefault();
    const normalizedCode = couponInput.trim().toLocaleUpperCase("de-DE");

    if (normalizedCode === DISCOUNT_CODE) {
      setCouponInput(DISCOUNT_CODE);
      setIsCouponApplied(true);
      setCouponMessage({
        type: "success",
        text: "NATUR10 ist aktiv: Du sparst 10% auf deine Artikel.",
      });
      return;
    }

    setIsCouponApplied(false);
    setCouponMessage({
      type: "error",
      text: "Dieser Gutscheincode ist leider nicht gültig.",
    });
  };

  const removeCoupon = () => {
    setCouponInput("");
    setIsCouponApplied(false);
    setCouponMessage(null);
  };

  if (!validCartItems.length) {
    return (
      <main className="cart-page cart-page--empty">
        <section className="cart-page__empty" aria-labelledby="empty-cart-heading">
          <span className="cart-page__empty-icon" aria-hidden="true">
            <FiShoppingBag />
          </span>
          <span className="cart-page__eyebrow">Deine Einkaufstasche</span>
          <h1 id="empty-cart-heading">Noch ist deine Tasche leer.</h1>
          <p>
            Entdecke langlebige Lieblingsstücke aus natürlichen Materialien und
            finde, was wirklich zu dir passt.
          </p>
          <Link className="cart-page__primary-link" to="/shop">
            Kollektion entdecken
          </Link>
          <div className="cart-page__empty-benefits" aria-label="Deine Vorteile">
            <span>
              <FiCheck aria-hidden="true" /> Faire Naturfasern
            </span>
            <span>
              <FiCheck aria-hidden="true" /> Einfache Retoure
            </span>
            <span>
              <FiCheck aria-hidden="true" /> Sicher bezahlen
            </span>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <header className="cart-page__header">
        <nav aria-label="Brotkrümelnavigation">
          <ol>
            <li>
              <Link to="/">Startseite</Link>
            </li>
            <li aria-hidden="true">/</li>
            <li aria-current="page">Einkaufstasche</li>
          </ol>
        </nav>
        <div>
          <h1>Deine Einkaufstasche</h1>
          <span>{itemCount} Artikel</span>
        </div>
      </header>

      <div className="cart-page__layout">
        <section className="cart-page__items" aria-label="Artikel in deiner Tasche">
          {validCartItems.map((item) => {
            const { product } = item;
            const lineTotal = Number(product.price) * item.quantity;
            const itemKey = `${item.id}-${item.size}-${item.color}`;

            return (
              <article className="cart-page__item" key={itemKey}>
                <Link
                  className="cart-page__item-image"
                  to={`/shopDetails/${product.id}`}
                  aria-label={`${product.name} ansehen`}
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    width="1024"
                    height="1365"
                    loading="lazy"
                    decoding="async"
                    onError={handleImageError}
                  />
                </Link>

                <div className="cart-page__item-content">
                  <div className="cart-page__item-copy">
                    <span>{product.subcategory}</span>
                    <h2>
                      <Link to={`/shopDetails/${product.id}`}>{product.name}</Link>
                    </h2>
                    <dl>
                      <div>
                        <dt>Farbe</dt>
                        <dd>{item.color}</dd>
                      </div>
                      <div>
                        <dt>Größe</dt>
                        <dd>{item.size}</dd>
                      </div>
                    </dl>
                    <p
                      className={`cart-page__stock ${
                        product.stock ? "is-available" : "is-unavailable"
                      }`}
                    >
                      <span aria-hidden="true" />
                      {product.stock
                        ? "Auf Lager · versandfertig in 2–4 Werktagen"
                        : "Derzeit nicht verfügbar"}
                    </p>
                  </div>

                  <div className="cart-page__item-price">
                    <strong>{formatPrice(lineTotal)}</strong>
                    {item.quantity > 1 && (
                      <small>{formatPrice(product.price)} je Artikel</small>
                    )}
                  </div>

                  <div className="cart-page__item-actions">
                    <div
                      className="cart-page__quantity"
                      role="group"
                      aria-label={`Menge für ${product.name}`}
                    >
                      <button
                        type="button"
                        aria-label={
                          item.quantity === 1
                            ? `${product.name} entfernen`
                            : `Menge von ${product.name} verringern`
                        }
                        onClick={() =>
                          updateQuantity(
                            item.id,
                            item.size,
                            item.color,
                            item.quantity - 1
                          )
                        }
                      >
                        <FiMinus aria-hidden="true" />
                      </button>
                      <output aria-live="polite" aria-label={`${item.quantity} Stück`}>
                        {item.quantity}
                      </output>
                      <button
                        type="button"
                        aria-label={`Menge von ${product.name} erhöhen`}
                        disabled={item.quantity >= 99}
                        onClick={() =>
                          updateQuantity(
                            item.id,
                            item.size,
                            item.color,
                            item.quantity + 1
                          )
                        }
                      >
                        <FiPlus aria-hidden="true" />
                      </button>
                    </div>

                    <button
                      type="button"
                      className="cart-page__remove"
                      onClick={() =>
                        removeFromCart(item.id, item.size, item.color)
                      }
                    >
                      <FiTrash2 aria-hidden="true" />
                      Entfernen
                    </button>
                  </div>
                </div>
              </article>
            );
          })}

          <Link className="cart-page__continue" to="/shop">
            <FiChevronLeft aria-hidden="true" />
            Weiter einkaufen
          </Link>
        </section>

        <aside className="cart-page__summary" aria-labelledby="summary-heading">
          <div className="cart-page__summary-card">
            <div className="cart-page__shipping-progress">
              <div>
                <FiTruck aria-hidden="true" />
                <p>
                  {hasFreeShipping ? (
                    <strong>Deine Lieferung ist kostenlos.</strong>
                  ) : (
                    <>
                      Noch <strong>{formatPrice(amountUntilFreeShipping)}</strong>{" "}
                      bis zum kostenlosen Versand.
                    </>
                  )}
                </p>
              </div>
              <span
                role="progressbar"
                aria-label="Fortschritt bis zum kostenlosen Versand"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={Math.round(shippingProgress)}
              >
                <span style={{ width: `${shippingProgress}%` }} />
              </span>
            </div>

            <h2 id="summary-heading">Bestellübersicht</h2>

            <form className="cart-page__coupon" onSubmit={handleCouponSubmit}>
              <label htmlFor="coupon-code">
                <FiTag aria-hidden="true" /> Gutscheincode
              </label>
              <div>
                <input
                  id="coupon-code"
                  type="text"
                  value={couponInput}
                  placeholder="Code eingeben"
                  autoComplete="off"
                  disabled={isCouponApplied}
                  onChange={(event) => {
                    setCouponInput(event.target.value);
                    setCouponMessage(null);
                  }}
                />
                {isCouponApplied ? (
                  <button type="button" onClick={removeCoupon}>
                    Entfernen
                  </button>
                ) : (
                  <button type="submit">Einlösen</button>
                )}
              </div>
              <p
                className={couponMessage ? `is-${couponMessage.type}` : ""}
                aria-live="polite"
              >
                {couponMessage?.text || "Tipp: NATUR10 spart 10%."}
              </p>
            </form>

            <dl className="cart-page__totals">
              <div>
                <dt>Zwischensumme</dt>
                <dd>{formatPrice(subtotal)}</dd>
              </div>
              {isCouponApplied && (
                <div className="cart-page__discount">
                  <dt>Rabatt NATUR10</dt>
                  <dd>− {formatPrice(discount)}</dd>
                </div>
              )}
              <div>
                <dt>Versand</dt>
                <dd>{hasFreeShipping ? "Kostenlos" : formatPrice(shipping)}</dd>
              </div>
              <div className="cart-page__total">
                <dt>Gesamtsumme</dt>
                <dd>{formatPrice(total)}</dd>
              </div>
            </dl>

            <p className="cart-page__vat">
              Preise inkl. MwSt. (enthalten: {formatPrice(includedVat)})
            </p>

            <Link
              className="cart-page__checkout"
              to="/checkout"
              state={{ discountCode: isCouponApplied ? DISCOUNT_CODE : null }}
            >
              Sicher zur Kasse
            </Link>

            <div className="cart-page__trust">
              <span>
                <FiLock aria-hidden="true" /> SSL-verschlüsselte Zahlung
              </span>
              <span>
                <FiCheck aria-hidden="true" /> 30 Tage Rückgaberecht
              </span>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
